import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import i18n from '@medical/shared/i18n';
import { extractMedicationPayload, getIntervalReminderTimes, SCHEDULE_TYPE } from '@medical/shared/utils/medicationUtils';

const MedicationReminderContext = createContext(null);

const REMINDER_DISMISSED_KEY = 'medication_reminder_dismissed';
const POST_MEDICATION_REMINDER_AT = 'post_medication_reminder_at';
const POST_MEDICATION_PAYLOAD = 'post_medication_payload';

function getReminderDismissed() {
  try {
    const raw = localStorage.getItem(REMINDER_DISMISSED_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const today = new Date().toDateString();
    if (parsed.date !== today) return {};
    return parsed.ids || {};
  } catch {
    return {};
  }
}

function setReminderDismissed(ids) {
  try {
    localStorage.setItem(REMINDER_DISMISSED_KEY, JSON.stringify({
      date: new Date().toDateString(),
      ids,
    }));
  } catch (e) {
    console.warn('localStorage setItem failed', e);
  }
}

export function MedicationReminderProvider({ children, token, user }) {
  const [prescriptions, setPrescriptions] = useState([]);
  const [pendingPostMedication, setPendingPostMedication] = useState(null);
  const [nextReminder, setNextReminder] = useState(null);
  const dismissedRef = useRef(getReminderDismissed());
  const thirtyMinTimeoutRef = useRef(null);

  const fetchPrescriptions = useCallback(async () => {
    if (!token || !user) return;
    try {
      const res = await axios.get('/api/messages', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.data?.success) return;
      const meds = (res.data.messages || [])
        .filter((m) => m.type === 'medication')
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setPrescriptions(meds);
    } catch (err) {
      console.warn('Failed to load prescriptions for reminders', err);
    }
  }, [token, user]);

  useEffect(() => {
    fetchPrescriptions();
    const interval = setInterval(fetchPrescriptions, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchPrescriptions]);

  // Request notification permission so reminders can show when app is in background
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Build reminder schedule from latest prescription (interval-based meds only)
  const reminderSchedule = React.useMemo(() => {
    const list = [];
    const latest = prescriptions[0];
    if (!latest?.message) return list;
    const { payload } = extractMedicationPayload(latest.message);
    if (!payload?.medications?.length) return list;
    payload.medications.forEach((item, idx) => {
      if (item.scheduleType !== SCHEDULE_TYPE.INTERVAL) return;
      const hours = item.intervalHours || 4;
      const first = item.firstDoseTime || '08:00';
      const times = getIntervalReminderTimes(hours, first);
      const name = [item.name, item.dose].filter(Boolean).join(' ') || `Medication ${idx + 1}`;
      list.push({
        prescriptionId: latest._id,
        medicationIndex: idx,
        medicationName: name,
        times,
      });
    });
    return list;
  }, [prescriptions]);

  // Next upcoming reminder time (for display)
  useEffect(() => {
    const now = new Date();
    const currentMins = now.getHours() * 60 + now.getMinutes();
    let next = null;
    let nextLabel = '';
    for (const s of reminderSchedule) {
      for (const { hour, minute } of s.times) {
        const reminderMins = hour * 60 + minute;
        if (reminderMins > currentMins + 1) {
          const minsUntil = reminderMins - currentMins;
          if (!next || minsUntil < next.minsUntil) {
            next = { at: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`, minsUntil, name: s.medicationName };
            nextLabel = `${s.medicationName} at ${next.at}`;
          }
        }
      }
    }
    setNextReminder(next ? { ...next, label: nextLabel } : null);
  }, [reminderSchedule]);

  // Check every minute if it's reminder time → show toast (and optional Notification)
  useEffect(() => {
    if (reminderSchedule.length === 0) return;
    const check = () => {
      const now = new Date();
      const today = new Date().toDateString();
      const dismissed = getReminderDismissed();
      if (dismissed.date !== today) {
        dismissedRef.current = {};
        setReminderDismissed({});
      }
      reminderSchedule.forEach((s) => {
        const id = `${s.prescriptionId}-${s.medicationIndex}-${now.getHours()}-${now.getMinutes()}`;
        if (dismissedRef.current[id]) return;
        const match = s.times.some(
          ({ hour, minute }) => now.getHours() === hour && now.getMinutes() === minute
        );
        if (match) {
          dismissedRef.current[id] = true;
          setReminderDismissed(dismissedRef.current);
          const message = i18n.t('medication.reminders.takeMedication', { name: s.medicationName });
          const recordAfter = i18n.t('medication.reminders.recordPainAfter');
          const iTookIt = i18n.t('medication.reminders.iTookIt');
          toast((toastId) => (
            <div>
              <p style={{ margin: 0, fontWeight: 600 }}>{message}</p>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: '#64748b' }}>
                {recordAfter}
              </p>
              <button
                type="button"
                onClick={() => {
                  toast.dismiss(toastId.id);
                  if (typeof window !== 'undefined' && window.MedicationReminderMarkTaken) {
                    window.MedicationReminderMarkTaken(s.medicationName);
                  }
                }}
                style={{
                  marginTop: '0.75rem',
                  padding: '0.5rem 1rem',
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {iTookIt}
              </button>
            </div>
          ), { duration: 60000 });
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            // Use service worker showNotification if available for better mobile support
            if (navigator.serviceWorker?.controller) {
              navigator.serviceWorker.ready
                .then((reg) =>
                  reg.showNotification(i18n.t('medication.reminders.title'), {
                    body: message,
                    icon: '/logo-mark.jpg',
                    badge: '/logo-mark.jpg',
                    tag: 'medication-reminder',
                    requireInteraction: true,
                    vibrate: [200, 100, 200],
                  })
                )
                .catch(() => new Notification(i18n.t('medication.reminders.title'), { body: message }));
            } else {
              new Notification(i18n.t('medication.reminders.title'), { body: message });
            }
          }
        }
      });
    };
    check();
    const interval = setInterval(check, 60 * 1000);
    return () => clearInterval(interval);
  }, [reminderSchedule]);

  const markTaken = useCallback((medicationName) => {
    const takenAt = new Date();
    setPendingPostMedication({ medicationName, takenAt: takenAt.toISOString() });
    axios.post('/api/medication-doses', {
      medicationName,
      takenAt: takenAt.toISOString()
    }).catch((error) => {
      console.warn('Could not save dose', error);
    });
    try {
      localStorage.setItem(POST_MEDICATION_REMINDER_AT, String(takenAt.getTime() + 30 * 60 * 1000));
      localStorage.setItem(POST_MEDICATION_PAYLOAD, JSON.stringify({ medicationName, takenAt: takenAt.toISOString() }));
    } catch (e) {
      console.warn('localStorage failed', e);
    }
    if (thirtyMinTimeoutRef.current) clearTimeout(thirtyMinTimeoutRef.current);
    thirtyMinTimeoutRef.current = setTimeout(() => {
      const recordNow = i18n.t('medication.reminders.recordPainNow');
      const recordNotif = i18n.t('medication.reminders.recordPainNotification');
      const notifTitle = i18n.t('medication.reminders.recordPain');
      toast(recordNow, { duration: 2000 });
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        // Use service worker showNotification if available
        if (navigator.serviceWorker?.controller) {
          navigator.serviceWorker.ready
            .then((reg) =>
              reg.showNotification(notifTitle, {
                body: recordNotif,
                icon: '/logo-mark.jpg',
                badge: '/logo-mark.jpg',
                tag: 'post-medication-reminder',
                requireInteraction: true,
                vibrate: [200, 100, 200],
              })
            )
            .catch(() => new Notification(notifTitle, { body: recordNotif }));
        } else {
          new Notification(notifTitle, { body: recordNotif });
        }
      }
    }, 30 * 60 * 1000);
  }, []);

  const clearPendingPostMedication = useCallback(() => {
    setPendingPostMedication(null);
    try {
      localStorage.removeItem(POST_MEDICATION_REMINDER_AT);
      localStorage.removeItem(POST_MEDICATION_PAYLOAD);
    } catch (e) {}
  }, []);

  // Expose markTaken for toast button (toast content runs outside React tree)
  useEffect(() => {
    window.MedicationReminderMarkTaken = markTaken;
    return () => { delete window.MedicationReminderMarkTaken; };
  }, [markTaken]);

  // Restore pending from localStorage (e.g. after refresh) if within last 2 hours
  useEffect(() => {
    try {
      const raw = localStorage.getItem(POST_MEDICATION_PAYLOAD);
      if (!raw) return;
      const payload = JSON.parse(raw);
      const takenAt = payload.takenAt ? new Date(payload.takenAt) : null;
      if (takenAt && Date.now() - takenAt.getTime() < 2 * 60 * 60 * 1000) {
        setPendingPostMedication({ medicationName: payload.medicationName, takenAt: payload.takenAt });
      }
    } catch (e) {}
  }, []);

  const value = {
    prescriptions,
    reminderSchedule,
    nextReminder,
    pendingPostMedication,
    markTaken,
    clearPendingPostMedication,
    fetchPrescriptions,
  };

  return (
    <MedicationReminderContext.Provider value={value}>
      {children}
    </MedicationReminderContext.Provider>
  );
}

export function useMedicationReminders() {
  const ctx = useContext(MedicationReminderContext);
  if (!ctx) {
    return {
      prescriptions: [],
      reminderSchedule: [],
      nextReminder: null,
      pendingPostMedication: null,
      markTaken: () => {},
      clearPendingPostMedication: () => {},
      fetchPrescriptions: () => {},
    };
  }
  return ctx;
}
